/** Automações, etiquetas (cadastro) e etapa do funil. */
import type { Automacao, AutomacaoEntrada, EtapaConversa, ExecucaoAutomacao } from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "./api";

const erro = (e: unknown) =>
  toast.error(e instanceof ErroApi ? e.message : "Não foi possível salvar.");

export const useAutomacoes = () =>
  useQuery({ queryKey: ["automacoes"], queryFn: () => api.get<Automacao[]>("/automacoes") });

export const useExecucoes = (automacaoId?: string) =>
  useQuery({
    queryKey: ["automacoes", "execucoes", automacaoId ?? "todas"],
    queryFn: () =>
      api.get<ExecucaoAutomacao[]>(
        `/automacoes/execucoes${automacaoId ? `?automacaoId=${automacaoId}` : ""}`,
      ),
  });

export function useAcoesAutomacao() {
  const qc = useQueryClient();
  const depois = () => void qc.invalidateQueries({ queryKey: ["automacoes"] });
  return {
    salvar: useMutation({
      mutationFn: (v: { id: string | null; dados: AutomacaoEntrada }) =>
        v.id
          ? api.put<Automacao>(`/automacoes/${v.id}`, v.dados)
          : api.post<Automacao>("/automacoes", v.dados),
      onSuccess: () => {
        depois();
        toast.success("Regra salva!");
      },
      onError: erro,
    }),
    remover: useMutation({
      mutationFn: (id: string) => api.del(`/automacoes/${id}`),
      onSuccess: depois,
      onError: erro,
    }),
  };
}

export function useAcoesEtiqueta() {
  const qc = useQueryClient();
  const depois = () => {
    void qc.invalidateQueries({ queryKey: ["etiquetas"] });
    void qc.invalidateQueries({ queryKey: ["conversas"] });
  };
  return {
    salvar: useMutation({
      mutationFn: (v: { id: string | null; nome: string; cor: string }) =>
        v.id
          ? api.put(`/etiquetas/${v.id}`, { nome: v.nome, cor: v.cor })
          : api.post("/etiquetas", { nome: v.nome, cor: v.cor }),
      onSuccess: depois,
      onError: erro,
    }),
    remover: useMutation({
      mutationFn: (id: string) => api.del(`/etiquetas/${id}`),
      onSuccess: depois,
      onError: erro,
    }),
  };
}

export function useMudarEtapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { conversaId: string; etapa: EtapaConversa }) =>
      api.put(`/conversas/${v.conversaId}/etapa`, { etapa: v.etapa }),
    onMutate: async (v) => {
      // Funil: o cartão muda de coluna na hora.
      await qc.cancelQueries({ queryKey: ["conversas", "funil"] });
      qc.setQueriesData<{ id: string; etapa: EtapaConversa }[]>(
        { queryKey: ["conversas", "funil"] },
        (l) => l?.map((c) => (c.id === v.conversaId ? { ...c, etapa: v.etapa } : c)),
      );
    },
    onError: (e) => {
      erro(e);
      void qc.invalidateQueries({ queryKey: ["conversas", "funil"] });
    },
    onSettled: (_r, _e, v) => {
      void qc.invalidateQueries({ queryKey: ["conversa", v.conversaId] });
      void qc.invalidateQueries({ queryKey: ["conversas"] });
    },
  });
}
